import { useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateHtmlFromNodes } from '@lexical/html';
import { $isTextNode } from 'lexical';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import PlaygroundNodes from './nodes/PlaygroundNodes';
import PlaygroundEditorTheme from './themes/PlaygroundEditorTheme';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingSectionAnchorPlugin from './plugins/FloatingSectionAnchorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
import CollapsiblePlugin from './plugins/CollapsiblePlugin';
import PageBreakPlugin from './plugins/PageBreakPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import PlaygroundAutoLinkPlugin from './plugins/AutoLinkPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';

import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import MarkdownPlugin from './plugins/MarkdownPlugin';
import TableActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TableCellResizerPlugin from './plugins/TableCellResizer';
import TableHoverActionsPlugin from './plugins/TableHoverActionsPlugin';
import DragDropPastePlugin from './plugins/DragDropPastePlugin';
import ColumnsLayoutPlugin from './plugins/ColumnsLayoutPlugin';
import EquationsPlugin from './plugins/EquationsPlugin';
import ExcalidrawPlugin from './plugins/ExcalidrawPlugin';
import ShortcutsPlugin from './plugins/ShortcutsPlugin';
import SectionAnchorPlugin from './plugins/SectionAnchorPlugin';

import { ToolbarContext } from './context/ToolbarContext';
import { SharedHistoryContext, useSharedHistoryContext } from './context/SharedHistoryContext';
import LexicalContentEditable from './ui/ContentEditable';

import './themes/PlaygroundEditorTheme.css';
import './index.css';

function onError(error) {
  console.error(error);
}

function HtmlPlugin({ onHtmlChange, onEditorStateJsonChange }) {
  const [editor] = useLexicalComposerContext();
  const onHtmlChangeRef = useRef(onHtmlChange);
  const onEditorStateJsonChangeRef = useRef(onEditorStateJsonChange);

  useEffect(() => { onHtmlChangeRef.current = onHtmlChange; }, [onHtmlChange]);
  useEffect(() => { onEditorStateJsonChangeRef.current = onEditorStateJsonChange; }, [onEditorStateJsonChange]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
      if (onEditorStateJsonChangeRef.current) {
        onEditorStateJsonChangeRef.current(editorState.toJSON());
      }
      if (onHtmlChangeRef.current) {
        editorState.read(() => {
          const html = $generateHtmlFromNodes(editor);
          onHtmlChangeRef.current(html);
        });
      }
    });
  }, [editor]);

  return null;
}

function EditorInner({ onHtmlChange, onUploadImage, onEditorStateJsonChange }) {
  const { historyState } = useSharedHistoryContext();
  const [floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);

  const onRef = (floatingAnchorElem) => {
    if (floatingAnchorElem !== null) {
      setFloatingAnchorElem(floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport = window.matchMedia('(max-width: 1025px)').matches;
      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);
    return () => window.removeEventListener('resize', updateViewPortWidth);
  }, [isSmallWidthViewport]);

  return (
    <>
      <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} onUploadImage={onUploadImage} />
      <div className="editor-container">
        <DragDropPastePlugin onUploadImage={onUploadImage} />
        <ClearEditorPlugin />
        <HtmlPlugin onHtmlChange={onHtmlChange} onEditorStateJsonChange={onEditorStateJsonChange} />
        <ComponentPickerPlugin onUploadImage={onUploadImage} />
        <HashtagPlugin />
        <PlaygroundAutoLinkPlugin />
        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller">
              <div className="editor" ref={onRef}>
                <LexicalContentEditable placeholder="Start writing your blog here..." />
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownPlugin />
        <CodeHighlightPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TablePlugin
          hasCellMerge={true}
          hasCellBackgroundColor={true}
          hasHorizontalScroll={true}
        />
        <TableCellResizerPlugin />
        <ImagesPlugin />
        <LinkPlugin />
        <ClickableLinkPlugin />
        <HorizontalRulePlugin />
        <TabIndentationPlugin />
        <CollapsiblePlugin />
        <PageBreakPlugin />
        <ColumnsLayoutPlugin />
        <EquationsPlugin />
        <ExcalidrawPlugin />
           <ShortcutsPlugin />
        <SectionAnchorPlugin />
        <HistoryPlugin externalHistoryState={historyState} />
        {floatingAnchorElem && !isSmallWidthViewport && (
          <>
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
        {floatingAnchorElem && (
          <>
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
            <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} setIsLinkEditMode={setIsLinkEditMode} />
            <FloatingSectionAnchorPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
        {floatingAnchorElem && (
          <>
            <TableActionMenuPlugin anchorElem={floatingAnchorElem} cellMerge={true} />
            <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
      </div>

    </>
  );
}

export default function Editor({ editorState: initialEditorState, onHtmlChange, onUploadImage, onEditorStateJsonChange }) {
  const makeInlineStyleConverter = (mandatoryFormat) => (domNode) => {
    const dataStyle = domNode.getAttribute('data-lex-style');
    if (!dataStyle) return null;

    const s = domNode.style;
    const fontWeight = s.fontWeight;
    const textDecoration = s.textDecoration ? s.textDecoration.split(' ') : [];
    const hasBold = fontWeight === '700' || fontWeight === 'bold';
    const hasStrikethrough = textDecoration.includes('line-through');
    const hasItalic = s.fontStyle === 'italic';
    const hasUnderline = textDecoration.includes('underline');
    const verticalAlign = s.verticalAlign;

    return {
      conversion: () => ({
        node: null,
        forChild: (child) => {
          if (!$isTextNode(child)) return child;
          // Re-apply the format this tag implies (e.g. bold for <strong>)
          if (mandatoryFormat && !child.hasFormat(mandatoryFormat)) child.toggleFormat(mandatoryFormat);
          // Apply any additional format flags derived from inline CSS
          if (hasBold && !child.hasFormat('bold')) child.toggleFormat('bold');
          if (hasStrikethrough && !child.hasFormat('strikethrough')) child.toggleFormat('strikethrough');
          if (hasItalic && !child.hasFormat('italic')) child.toggleFormat('italic');
          if (hasUnderline && !child.hasFormat('underline')) child.toggleFormat('underline');
          if (verticalAlign === 'sub' && !child.hasFormat('subscript')) child.toggleFormat('subscript');
          if (verticalAlign === 'super' && !child.hasFormat('superscript')) child.toggleFormat('superscript');
          // Apply CSS properties that default converters ignore
          const existing = child.getStyle();
          child.setStyle(existing ? `${existing}; ${dataStyle}` : dataStyle);
          return child;
        },
      }),
      priority: 2,
    };
  };

  const initialConfig = {
    editorState: initialEditorState,
    namespace: 'Playground',
    nodes: [...PlaygroundNodes],
    onError,
    theme: PlaygroundEditorTheme,
    html: {
      import: {
        span:   makeInlineStyleConverter(null),
        strong: makeInlineStyleConverter('bold'),
        em:     makeInlineStyleConverter('italic'),
        // code:   makeInlineStyleConverter('code'),
        mark:   makeInlineStyleConverter('highlight'),
        sub:    makeInlineStyleConverter('subscript'),
        sup:    makeInlineStyleConverter('superscript'),
      },
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <SharedHistoryContext>
        <ToolbarContext>
          <div className="editor-shell">
            <EditorInner onHtmlChange={onHtmlChange} onUploadImage={onUploadImage} onEditorStateJsonChange={onEditorStateJsonChange} />
          </div>
        </ToolbarContext>
      </SharedHistoryContext>
    </LexicalComposer>
  );
}
